import React, { useState, useEffect, useMemo, useRef } from "react";
import { StyledForm } from "lib/styles/general";
import { CTAButton } from "lib/components/CTAButton";
import { CheckboxButton } from "lib/components/Checkbox";
import { KeyValue } from "lib/components/KeyValue";
import { Input } from "lib/components/Input";
import { Select } from "lib/components/Select";
import { fetchData, useFetch, Error } from "lib/helpers/fetchData";
import { Spinner } from "lib/components/Spinner";
import { getApiUrl } from "lib/helpers/getApiUrl";
import { displayDate } from "lib/helpers/displayFormats";
import { ErrorModal } from "lib/components/Modal";

const MINUTE_IN_MS = 60 * 1000;
const THIRTY_DAYS_IN_MINUTES = 30 * 24 * 60;

const getJiraInstances = async () => {
  return await fetchData(`${getApiUrl("JIRA_MANAGEMENT_API")}/instances`);
};

const getCustomerConfig = async ({ customerId }) => {
  try {
    const existingCustomer = await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/${customerId}`);
    return existingCustomer;
  } catch (err) {
    if (err.statusCode === 404) {
      return undefined;
    }

    throw err;
  }
};

const loadDefaultConfigs = async () => fetchData(`${getApiUrl("TICKET_SERVICE")}/defaultSlaConfigurations`);

const BREACH_FIELDS = [
  { key: "p0Breach", defaultKey: "p0BreachMs", label: "P0 Breach Time (min)" },
  { key: "p1Breach", defaultKey: "p1BreachMs", label: "P1 Breach Time (min)" },
  { key: "p2Breach", defaultKey: "p2BreachMs", label: "P2 Breach Time (min)" },
  { key: "p3Breach", defaultKey: "p3BreachMs", label: "P3 Breach Time (min)" },
  { key: "phishBreach", defaultKey: "phishBreachMs", label: "Phish Breach Time (min)" },
];

const INTEGER_PATTERN = /^\d+$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getToday = () => displayDate(new Date());

const toDateInputValue = (value) => value?.split("T")[0] ?? getToday();

const isValidDate = (value) => {
  if (!DATE_PATTERN.test(value)) return false;

  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
};

const toMinutesValue = (value) => value == null ? "" : String(Math.max(1, Math.floor(value / MINUTE_IN_MS)));

const toDisplayDate = (value) => {
  if (!value) return "Not set";

  const [year, month, day] = value.split("T")[0].split("-");
  return day && month && year ? `${day} / ${month} / ${year}` : value;
};

const getBreachValue = (sla, defaultConfig, { key, defaultKey }) => {
  const value = sla?.customSlaEnabled ? sla[key] ?? defaultConfig?.[defaultKey] : defaultConfig?.[defaultKey];
  return toMinutesValue(value);
};

const getDefaultBreachValues = (defaultConfig) =>
  Object.fromEntries(BREACH_FIELDS.map(({ key, defaultKey }) => [key, toMinutesValue(defaultConfig?.[defaultKey])]));

const buildFormState = (data, defaultConfig) => {
  const sla = data ?? {};
  const defaultSlaConfigurationId = defaultConfig?.id ?? "";

  return {
    newSlaEnabled: !!sla.newSlaEnabled,
    customSlaEnabled: !!sla.customSlaEnabled,
    defaultSlaConfigurationId: sla.defaultSlaConfigurationId === defaultSlaConfigurationId ? sla.defaultSlaConfigurationId : defaultSlaConfigurationId,
    slaMetricsCutoffDate: toDateInputValue(sla.slaMetricsCutoffDate),
    ...Object.fromEntries(BREACH_FIELDS.map((field) => [field.key, getBreachValue(sla, defaultConfig, field)])),
  };
};

const buildOriginalBreachValues = (data, defaultConfig) =>
  Object.fromEntries(BREACH_FIELDS.map(({ key, defaultKey }) => [key, data?.customSlaEnabled ? data[key] ?? defaultConfig?.[defaultKey] : defaultConfig?.[defaultKey]]));

const isBreachFieldEdited = (formState, originalBreachValues, changedBreachFields, configs, { key, defaultKey }) => {
  if (!formState.customSlaEnabled) return false;

  const defaultConfig = configs.find((config) => config.id === formState.defaultSlaConfigurationId);
  const currentValue = getBreachPostValue(formState, originalBreachValues, changedBreachFields, key);
  return currentValue !== defaultConfig?.[defaultKey];
};

const isConfigurationEdited = (formState, originalBreachValues, changedBreachFields, configs) =>
  BREACH_FIELDS.some((field) => isBreachFieldEdited(formState, originalBreachValues, changedBreachFields, configs, field));

const getConfigurationDisplayName = (formState, originalBreachValues, changedBreachFields, configs) => {
  const name = configs.find((config) => config.id === formState.defaultSlaConfigurationId)?.name || "Not set";
  return isConfigurationEdited(formState, originalBreachValues, changedBreachFields, configs) && name !== "Not set" ? `${name} (edited)` : name;
};

const getBreachPostValue = (formState, originalBreachValues, changedBreachFields, key) => {
  if (!changedBreachFields[key] && originalBreachValues[key] !== undefined) return originalBreachValues[key];
  if (!INTEGER_PATTERN.test(formState[key])) return Number.NaN;
  return Number.parseInt(formState[key], 10) * MINUTE_IN_MS;
};

const FieldError = ({ error, margin = "-0.75rem 0 10px" }) =>
  error ? <p style={{ color: "rgba(255, 0, 0, 0.8)", fontSize: "1.2rem", margin }}>{error}</p> : null;

export const Sla = ({ data, reload }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { data: customerData, isLoading: customerDataLoading, error: customerDataError } = useFetch(getCustomerConfig, { customerId: data.id });
  const { data: defaultConfigs, isLoading: defaultConfigsLoading, error: defaultConfigsError } = useFetch(loadDefaultConfigs);
  const configs = useMemo(() => defaultConfigs ?? [], [defaultConfigs]);
  const selectedDefaultConfig = configs.find((config) => config.id === customerData?.defaultSlaConfigurationId) ?? configs[0];
  const [formState, setFormState] = useState(buildFormState(customerData, selectedDefaultConfig));
  const [savedState, setSavedState] = useState(buildFormState(customerData, selectedDefaultConfig));
  const [originalBreachValues, setOriginalBreachValues] = useState(buildOriginalBreachValues(customerData, selectedDefaultConfig));
  const [savedOriginalBreachValues, setSavedOriginalBreachValues] = useState(buildOriginalBreachValues(customerData, selectedDefaultConfig));
  const [changedBreachFields, setChangedBreachFields] = useState({});
  const hasInitialisedForm = useRef(false);

  useEffect(() => {
    if (hasInitialisedForm.current || customerDataLoading || defaultConfigsLoading || defaultConfigs === undefined) return;

    const defaultConfig = configs.find((config) => config.id === customerData?.defaultSlaConfigurationId) ?? configs[0];
    const initialCustomerData = customerData ?? {};

    setFormState(buildFormState(initialCustomerData, defaultConfig));
    setSavedState(buildFormState(initialCustomerData, defaultConfig));
    const initialBreachValues = buildOriginalBreachValues(initialCustomerData, defaultConfig);
    setOriginalBreachValues(initialBreachValues);
    setSavedOriginalBreachValues(initialBreachValues);
    setChangedBreachFields({});
    hasInitialisedForm.current = true;
  }, [customerData, customerDataLoading, configs, defaultConfigs, defaultConfigsLoading]);

  const validate = () => {
    const newErrors = {};
    if (formState.newSlaEnabled) {
      if (!formState.slaMetricsCutoffDate) {
        newErrors.slaMetricsCutoffDate = "Metrics reset date is required";
      } else if (!isValidDate(formState.slaMetricsCutoffDate)) {
        newErrors.slaMetricsCutoffDate = "Enter a valid date";
      }

      if (!formState.defaultSlaConfigurationId) {
        newErrors.defaultSlaConfigurationId = "Configuration is required";
      }

      if (formState.customSlaEnabled) {
        BREACH_FIELDS.forEach(({ key }) => {
          if (!formState[key]) {
            newErrors[key] = "Required";
          } else if (!INTEGER_PATTERN.test(formState[key])) {
            newErrors[key] = "Must be a whole number";
          }
          else {
            const value = Number.parseInt(formState[key], 10);
            if (value < 1 || value > THIRTY_DAYS_IN_MINUTES) {
              newErrors[key] = `Must be in range 1 - ${THIRTY_DAYS_IN_MINUTES}`;
            }
          }
        });
      }
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const formStateToPostData = async () => {
    const jiraInstances = await getJiraInstances();
    const instance = jiraInstances.find((i) => i.id === data.connectors.jira.instanceId);

    const baseData = {
      customerId: data.id,
      customerName: data.name,
      jiraInstanceName: instance.name,
      jiraInstanceDomain: instance.domain,
      newSlaEnabled: formState.newSlaEnabled,
      customSlaEnabled: formState.customSlaEnabled,
    };

    const newSlaData = formState.customSlaEnabled ? {
      defaultSlaConfigurationId: formState.defaultSlaConfigurationId,
      ...Object.fromEntries(BREACH_FIELDS.map(({ key }) => [
        key,
        getBreachPostValue(formState, originalBreachValues, changedBreachFields, key),
      ])),
    } : {
      defaultSlaConfigurationId: formState.defaultSlaConfigurationId,
    };

    const customData = formState.newSlaEnabled ? { ...newSlaData, slaMetricsCutoffDate: formState.slaMetricsCutoffDate } : {};

    return { ...baseData, ...customData };
  }

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);

    try {
      const postData = await formStateToPostData();
      await fetchData(`${getApiUrl("TICKET_SERVICE")}/customers/update-sla-configuration?group=${data.groupName}`, JSON.stringify(postData), "POST");
      setSavedState(formState);
      if (formState.customSlaEnabled) {
        const savedBreachValues = Object.fromEntries(BREACH_FIELDS.map(({ key }) => [key, postData[key]]));
        setOriginalBreachValues(savedBreachValues);
        setSavedOriginalBreachValues(savedBreachValues);
      } else {
        const defaultConfig = configs.find((config) => config.id === formState.defaultSlaConfigurationId);
        const defaultBreachValues = buildOriginalBreachValues(undefined, defaultConfig);
        setOriginalBreachValues(defaultBreachValues);
        setSavedOriginalBreachValues(defaultBreachValues);
      }
      setChangedBreachFields({});
      setIsEditMode(false);
      reload?.();
    } catch (err) {
      console.error("Unable to save SLA configuration", err);
      setErrors({ form: "An error occurred while saving. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormState(savedState);
    setOriginalBreachValues(savedOriginalBreachValues);
    setChangedBreachFields({});
    setErrors({});
    setIsEditMode(false);
  };

  const toggleNewSla = (checked) => {
    const defaultConfig = configs.find((config) => config.id === formState.defaultSlaConfigurationId) ?? configs[0];

    if (!checked) {
      const defaultBreachValues = buildOriginalBreachValues(undefined, defaultConfig);
      setOriginalBreachValues(defaultBreachValues);
      setChangedBreachFields({});
    }

    setFormState((prev) => ({
      ...prev,
      newSlaEnabled: checked,
      slaMetricsCutoffDate: checked ? prev.slaMetricsCutoffDate || getToday() : prev.slaMetricsCutoffDate,
      customSlaEnabled: false,
      defaultSlaConfigurationId: prev.defaultSlaConfigurationId || defaultConfig?.id || "",
      ...(!checked && getDefaultBreachValues(defaultConfig)),
    }));
    if (!checked) setErrors({});
  };

  const updateDefaultConfiguration = (configurationId) => {
    const defaultConfig = configs.find((config) => config.id === configurationId);

    setOriginalBreachValues(buildOriginalBreachValues(undefined, defaultConfig));
    setChangedBreachFields({});
    setFormState((prev) => ({
      ...prev,
      customSlaEnabled: false,
      defaultSlaConfigurationId: configurationId,
      ...getDefaultBreachValues(defaultConfig),
    }));
    setErrors({});
  };

  const resetBreachValues = () => updateDefaultConfiguration(formState.defaultSlaConfigurationId);

  const handleStartEnableNewSla = () => {
    toggleNewSla(true);
    setIsEditMode(true);
  };

  if (customerDataLoading || defaultConfigsLoading) return <Spinner />;
  if (customerDataError) return <Error message={customerDataError.message ?? "Unable to retrieve customer configuration"} />;
  if (defaultConfigsError) return <Error message={defaultConfigsError.message ?? "Unable to retrieve default SLA configurations"} />;

  return (
    <div style={{ position: "relative" }}>
      {!isEditMode && formState.newSlaEnabled && (
        <CTAButton
          style={{ position: "absolute", right: 0, top: 0, zIndex: 99 }}
          onClick={() => setIsEditMode(true)}
        >
          Edit
        </CTAButton>
      )}
      <StyledForm>
        <section style={{ paddingTop: 0 }}>
          {!isEditMode ? (
            <>
              {!formState.newSlaEnabled ? (
                <CheckboxButton
                  name="newSlaEnabled"
                  subtle
                  showWhenNull
                  label="Enable new SLA functionality"
                  defaultChecked={false}
                  onChange={(e) => e.target.checked && handleStartEnableNewSla()}
                />
              ) : (
                <>
                  <KeyValue text="New SLA Functionality" value="Enabled" />
                  <KeyValue text="Metrics Reset Date" value={toDisplayDate(formState.slaMetricsCutoffDate)} />
                  <KeyValue text="Configuration" value={getConfigurationDisplayName(formState, originalBreachValues, changedBreachFields, configs)} />
                </>
              )}
            </>
          ) : (
            <>
              <CheckboxButton
                key={String(formState.newSlaEnabled)}
                name="newSlaEnabled"
                subtle
                showWhenNull
                label="Enable new SLA functionality"
                defaultChecked={formState.newSlaEnabled}
                onChange={(e) => toggleNewSla(e.target.checked)}
              />
              {formState.newSlaEnabled && (
                <>
                  <div style={{ marginBottom: "1rem" }}>
                    <h3 style={{ fontSize: "1.33rem", margin: "0 0 0.3rem", color: "inherit" }}>Metrics Reset Date</h3>
                    <Input
                      label=""
                      aria-label="Metrics Reset Date"
                      type="date"
                      value={formState.slaMetricsCutoffDate}
                      onChange={(e) => {
                        setErrors((prev) => ({ ...prev, slaMetricsCutoffDate: null }));
                        setFormState((prev) => ({ ...prev, slaMetricsCutoffDate: e.target.value }));
                      }}
                      error={errors.slaMetricsCutoffDate}
                    />
                    <p style={{ fontSize: "1.1rem", margin: "-0.5rem 0 0", color: "inherit", opacity: 0.75 }}>
                      The date the customer's SLA and key metrics will be recalculated from. On the Clarity dashboard, if a customer selects a date range before this date, some metrics will show as unavailable.
                    </p>
                  </div>
                  <Input
                    label="Configuration"
                    onChange={(e) => {
                      setErrors((prev) => ({ ...prev, defaultSlaConfigurationId: null }));
                      updateDefaultConfiguration(e.target.value);
                    }}
                  >
                    <Select
                      name="defaultSlaConfigurationId"
                      style={{ margin: "0.3rem 0 1rem", width: "100%" }}
                      value={formState.defaultSlaConfigurationId}
                    >
                      {configs.map((config) => (
                        <option key={config.id} value={config.id}>
                          {isConfigurationEdited(formState, originalBreachValues, changedBreachFields, configs) && config.id === formState.defaultSlaConfigurationId ? `${config.name} (edited)` : config.name}
                        </option>
                      ))}
                    </Select>
                  </Input>
                  <FieldError error={errors.defaultSlaConfigurationId} />
                  <div style={{ borderLeft: "0.25rem solid rgba(128, 128, 128, 0.35)", margin: "0 0 1rem 1rem", paddingLeft: "2rem" }}>
                    {BREACH_FIELDS.map((field) => (
                      <div key={field.key} style={{ position: "relative" }}>
                        <Input
                          label={field.label}
                          type="text"
                          inputMode="numeric"
                          value={formState[field.key]}
                          placeholder="Enter whole number"
                          onChange={(e) => {
                            const nextChangedFields = { ...changedBreachFields, [field.key]: true };
                            setChangedBreachFields(nextChangedFields);
                            setFormState((previousFormState) => {
                              const nextFormState = { ...previousFormState, customSlaEnabled: true, [field.key]: e.target.value };
                              return {
                                ...nextFormState,
                                customSlaEnabled: isConfigurationEdited(nextFormState, originalBreachValues, nextChangedFields, configs),
                              };
                            });
                          }}
                          error={errors[field.key]}
                        />
                        {isBreachFieldEdited(formState, originalBreachValues, changedBreachFields, configs, field) && (
                          <span style={{ position: "absolute", right: "0.75rem", bottom: "1.65rem", fontSize: "1.1rem", opacity: 0.65 }}>
                            edited
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </StyledForm>
      {isEditMode && (
        <div style={{ display: "flex", justifyContent: formState.customSlaEnabled ? "space-between" : "flex-end", alignItems: "center", gap: "1rem" }}>
          {formState.customSlaEnabled && (
            <CTAButton isSecondary onClick={resetBreachValues}>
              Reset
            </CTAButton>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
            <CTAButton isSecondary onClick={handleCancel}>
              Cancel
            </CTAButton>
            <CTAButton isLoading={isLoading} onClick={handleSubmit}>
              Save
            </CTAButton>
          </div>
        </div>
      )}
      {errors?.form && <ErrorModal data-testid="error" error={errors.form} hide={() => setErrors({})} />}
    </div>
  );
};
